/**
 * Cutting the pieces somebody marked out of one picture.
 *
 * No detection here on purpose. Finding the rectangles automatically was tried
 * and it half works: it locates them, but on a busy table it will not find
 * their borders to the pixel, and a crop that is nearly right is a crop you
 * have to redo by hand anyway. Drawing the boxes takes seconds and is right the
 * first time, so that is what the tool does.
 */

import { encodeCanvas } from '../shared/imageOutputFormats';

let counter = 0;

export function newSelectionId() {
    counter += 1;
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return `sel_${crypto.randomUUID()}`;
    return `sel_${Date.now().toString(36)}_${counter}`;
}

const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);

/** A drag may run right-to-left or bottom-to-top; a rectangle may not. */
export function rectFromDrag(a, b) {
    return {
        x: Math.min(a.x, b.x),
        y: Math.min(a.y, b.y),
        w: Math.abs(b.x - a.x),
        h: Math.abs(b.y - a.y),
    };
}

/** Keep a rectangle inside the picture, and big enough to be worth cutting. */
export function clampRect(rect, imageW, imageH, min = 8) {
    const w = clamp(rect.w, min, imageW);
    const h = clamp(rect.h, min, imageH);
    return {
        ...rect,
        w,
        h,
        x: clamp(rect.x, 0, imageW - w),
        y: clamp(rect.y, 0, imageH - h),
    };
}

/** A selection's four corners in image space, turned about its own centre. */
export function selectionCorners(sel) {
    const cx = sel.x + sel.w / 2;
    const cy = sel.y + sel.h / 2;
    const rad = ((sel.rotation || 0) * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    return [
        [-sel.w / 2, -sel.h / 2],
        [sel.w / 2, -sel.h / 2],
        [sel.w / 2, sel.h / 2],
        [-sel.w / 2, sel.h / 2],
    ].map(([dx, dy]) => [cx + dx * cos - dy * sin, cy + dx * sin + dy * cos]);
}

/**
 * One marked-out piece, as its own canvas.
 *
 * Cut at the picture's own resolution — the selection is in image pixels, so
 * what comes out is exactly the pixels that were inside the box, never resized
 * up into softness. `maxEdge` only ever scales DOWN, for a phone photo whose
 * pieces would otherwise each be enormous.
 */
export function cropRect(image, rect, { maxEdge = 2400 } = {}) {
    const w = Math.max(1, Math.round(rect.w));
    const h = Math.max(1, Math.round(rect.h));
    const scale = Math.min(1, maxEdge / Math.max(w, h));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(w * scale));
    canvas.height = Math.max(1, Math.round(h * scale));
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingQuality = 'high';

    const angle = rect.rotation || 0;
    if (!angle) {
        ctx.drawImage(image, Math.round(rect.x), Math.round(rect.y), w, h, 0, 0, canvas.width, canvas.height);
        return canvas;
    }

    // A turned selection is cut by turning the PICTURE the other way about the
    // selection's centre and taking the upright rectangle that is then under
    // it. Straightening a crooked photo and cropping it are the same operation
    // done once, which is why the piece comes out square rather than square
    // with a tilted picture inside it.
    const cx = rect.x + rect.w / 2;
    const cy = rect.y + rect.h / 2;
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.scale(scale, scale);
    ctx.rotate((-angle * Math.PI) / 180);
    ctx.translate(-cx, -cy);
    ctx.drawImage(image, 0, 0);
    return canvas;
}

/** A canvas as a file, ready to download or to put in a zip. */
export function canvasToBlob(canvas, type = 'image/jpeg', quality = 0.94) {
    return new Promise((resolve) => { canvas.toBlob(resolve, type, quality); });
}

/**
 * One cut-out as a file, in the format that was asked for.
 *
 * Not a bare `toBlob`: a browser that cannot write the format hands back a PNG
 * without saying so, which produced files named `.avif` that were PNG inside
 * and would not open. The shared encoder checks what actually came out, and
 * reaches for the WebAssembly AVIF encoder that Chrome's canvas lacks.
 *
 * "Original" re-encodes to the source's own type, so a PNG cut out of a PNG
 * stays a PNG.
 *
 * @returns {Promise<{ blob: Blob, ext: string }>}
 */
export async function cropToBlob(canvas, format, sourceType = '') {
    const target = format === 'original' ? extensionFromMime(sourceType) : format;
    const { blob, ext } = await encodeCanvas(canvas, target, { quality: 0.94 });
    return { blob, ext };
}

const FORMAT_MIME = {
    jpg: 'image/jpeg',
    png: 'image/png',
    webp: 'image/webp',
    avif: 'image/avif',
};

/**
 * What a cut-out is written as.
 *
 * "original" means the type the picture came in as — a PNG cut out of a PNG
 * stays a PNG — which is why the source type is needed here rather than just
 * the chosen format.
 */
export function mimeOf(format, sourceType = '') {
    if (format === 'original') {
        return FORMAT_MIME[extensionFromMime(sourceType)] || 'image/jpeg';
    }
    return FORMAT_MIME[format] || 'image/jpeg';
}

export function extensionOf(format, sourceType = '') {
    if (format === 'original') return extensionFromMime(sourceType);
    return format === 'jpg' ? 'jpg' : format;
}

function extensionFromMime(mime) {
    const found = Object.entries(FORMAT_MIME).find(([, value]) => value === mime);
    return found ? found[0] : 'jpg';
}
